const siteData = require("../data/siteData");

const stripWhitespace = (value) => value.replace(/\s+/g, " ").trim();

const truncate = (value, limit) => {
  const cleanValue = stripWhitespace(value);
  if (cleanValue.length <= limit) return cleanValue;
  return `${cleanValue.slice(0, limit - 1).trimEnd()}…`;
};

const absoluteUrl = (path = "") => {
  try {
    return new URL(path, siteData.siteUrl).toString();
  } catch (error) {
    return `${siteData.siteUrl}${path.startsWith("/") ? path : `/${path}`}`;
  }
};

const normalizePath = (path = "") => {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
};

// ─── Core Schema Builders ───

const createBreadcrumbList = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: absoluteUrl(normalizePath(item.path)),
  })),
});

const createOrganizationSchema = ({ logoUrl = siteData.defaultShareImage, sameAs = siteData.sameAs } = {}) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${siteData.siteUrl}/#organization`,
  name: siteData.siteName,
  alternateName: ["QBITS9", "Qbits9"],
  url: siteData.siteUrl,
  logo: {
    "@type": "ImageObject",
    url: absoluteUrl(logoUrl),
    width: 512,
    height: 512,
  },
  image: absoluteUrl(logoUrl),
  description: siteData.description,
  telephone: siteData.phoneDisplay,
  address: {
    "@type": "PostalAddress",
    ...siteData.address,
  },
  // GEO: foundingDate for entity authority
  foundingDate: siteData.foundingDate || "2022",
  foundingLocation: {
    "@type": "Place",
    name: "Surat, Gujarat, India",
  },
  // GEO: knowsAbout for AI topic association
  knowsAbout: siteData.knowsAbout || [
    "Artificial Intelligence", "Machine Learning", "Workflow Automation",
    "AI Automation", "Web Development", "Mobile App Development",
    "SaaS Development", "ERP Development", "Custom Software Development",
  ],
  // GEO: numberOfEmployees for entity completeness
  numberOfEmployees: {
    "@type": "QuantitativeValue",
    minValue: 10,
    maxValue: 50,
  },
  sameAs,
  // GEO: slogan for brand recognition
  slogan: "Automate Repetitive Work. Connect Your Systems. Scale With Confidence.",
});

const createLocalBusinessSchema = ({ logoUrl = siteData.defaultShareImage } = {}) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${siteData.siteUrl}/#localbusiness`,
  name: siteData.siteName,
  url: siteData.siteUrl,
  image: absoluteUrl(logoUrl),
  logo: absoluteUrl(logoUrl),
  telephone: siteData.phoneDisplay,
  address: {
    "@type": "PostalAddress",
    ...siteData.address,
  },
  // GEO: geo coordinates for local search & AI
  geo: {
    "@type": "GeoCoordinates",
    latitude: 21.1702,
    longitude: 72.8311,
  },
  openingHours: siteData.openingHours,
  // GEO: expanded areaServed for international AI visibility
  areaServed: [
    { "@type": "Country", "name": "India" },
    { "@type": "Country", "name": "United States" },
    { "@type": "Country", "name": "United Kingdom" },
    { "@type": "Country", "name": "United Arab Emirates" },
    { "@type": "Country", "name": "Australia" },
    { "@type": "Country", "name": "Canada" },
  ],
  priceRange: "$$",
  currenciesAccepted: "INR, USD",
  paymentAccepted: "Bank Transfer, UPI, Credit Card",
});

// GEO: ProfessionalService schema — AI engines use this to understand service providers
const createProfessionalServiceSchema = ({ logoUrl = siteData.defaultShareImage } = {}) => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${siteData.siteUrl}/#professionalservice`,
  name: siteData.siteName,
  alternateName: ["QBITS9", "Qbits9"],
  url: siteData.siteUrl,
  logo: absoluteUrl(logoUrl),
  image: absoluteUrl(logoUrl),
  description: siteData.description,
  telephone: siteData.phoneDisplay,
  address: {
    "@type": "PostalAddress",
    ...siteData.address,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 21.1702,
    longitude: 72.8311,
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    opens: "10:00",
    closes: "19:00",
  },
  priceRange: "$$",
  areaServed: "Worldwide",
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "QBits9 Services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "AI Automation Services",
          url: absoluteUrl("/services/ai-automation"),
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Web & Mobile App Development",
          url: absoluteUrl("/services/web-mobile-development"),
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "SaaS & ERP Development",
          url: absoluteUrl("/services/saas-erp-development"),
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Desktop Software Development",
          url: absoluteUrl("/services/desktop-software"),
        },
      },
    ],
  },
  sameAs: siteData.sameAs,
  knowsAbout: siteData.knowsAbout || [],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "50",
    bestRating: "5",
    worstRating: "1",
  },
});

const createWebSiteSchema = ({ searchUrl } = {}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${siteData.siteUrl}/#website`,
    name: siteData.siteName,
    alternateName: ["QBITS9", "Qbits9", "QBits 9"],
    url: siteData.siteUrl,
    description: siteData.description,
    publisher: {
      "@type": "Organization",
      name: siteData.siteName,
      url: siteData.siteUrl,
    },
    inLanguage: "en",
  };

  // GEO: SearchAction for sitelinks search box
  if (searchUrl) {
    schema.potentialAction = {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: searchUrl,
      },
      "query-input": "required name=search_term_string",
    };
  }

  return schema;
};

const createWebPageSchema = ({
  name,
  description,
  path,
  type = "WebPage",
  imageUrl,
  speakableSelectors = [],
  breadcrumbs = [],
  datePublished,
  dateModified,
  keywords = [],
} = {}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": type,
    name,
    description,
    url: absoluteUrl(normalizePath(path)),
    isPartOf: {
      "@type": "WebSite",
      "@id": `${siteData.siteUrl}/#website`,
      name: siteData.siteName,
      url: siteData.siteUrl,
    },
    // GEO: inLanguage for AI language understanding
    inLanguage: "en",
  };

  if (imageUrl) {
    schema.primaryImageOfPage = {
      "@type": "ImageObject",
      url: absoluteUrl(imageUrl),
    };
  }

  // GEO: Speakable for voice assistants & AI answers
  if (speakableSelectors.length > 0) {
    schema.speakable = {
      "@type": "SpeakableSpecification",
      cssSelector: speakableSelectors,
    };
  }

  if (breadcrumbs.length > 0) {
    schema.breadcrumb = {
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: absoluteUrl(normalizePath(item.path)),
      })),
    };
  }

  // GEO: date signals for content freshness
  if (datePublished) schema.datePublished = datePublished;
  if (dateModified) schema.dateModified = dateModified;

  // GEO: keywords for AI topic matching
  if (keywords.length > 0) schema.keywords = keywords.join(", ");

  return schema;
};

const createServiceSchema = ({
  name,
  description,
  path,
  serviceType,
  areaServed = "India",
  imageUrl,
  sameAs = siteData.sameAs,
  keywords = [],
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    serviceType,
    description,
    url: absoluteUrl(normalizePath(path)),
    areaServed,
    provider: {
      "@type": "Organization",
      "@id": `${siteData.siteUrl}/#organization`,
      name: siteData.siteName,
      url: siteData.siteUrl,
      logo: absoluteUrl(siteData.defaultShareImage),
      sameAs,
      address: {
        "@type": "PostalAddress",
        ...siteData.address,
      },
      telephone: siteData.phoneDisplay,
    },
    ...(imageUrl ? { image: absoluteUrl(imageUrl) } : {}),
  };

  // GEO: keywords for AI service matching
  if (keywords.length > 0) schema.keywords = keywords.join(", ");

  return schema;
};

const createFaqPageSchema = ({ faqs = [], path, name, description }) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
  url: absoluteUrl(normalizePath(path)),
  name,
  description,
});

const createHowToSchema = ({ name, description, path, steps = [] }) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name,
  description,
  url: absoluteUrl(normalizePath(path)),
  step: steps.map((step, index) => ({
    "@type": "HowToStep",
    position: index + 1,
    name: step.title,
    text: step.description,
  })),
});

const createContactPageSchema = ({ path = "/contact" } = {}) => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact QBITS9",
  description: "Get in touch with QBits9 for AI automation, custom software development, and digital transformation services.",
  url: absoluteUrl(normalizePath(path)),
  mainEntity: {
    "@type": "Organization",
    "@id": `${siteData.siteUrl}/#organization`,
    name: siteData.siteName,
    url: siteData.siteUrl,
    telephone: siteData.phoneDisplay,
    address: {
      "@type": "PostalAddress",
      ...siteData.address,
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: siteData.phoneDisplay,
      contactType: "sales",
      availableLanguage: ["English", "Hindi", "Gujarati"],
      areaServed: "Worldwide",
    },
  },
});

const createBlogPostingSchema = ({ post, path, imageUrl }) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title,
  description: post.metaDescription || post.excerpt,
  image: absoluteUrl(imageUrl || siteData.defaultShareImage),
  datePublished: post.datePublished,
  dateModified: post.dateModified || post.datePublished,
  author: {
    "@type": post.authorType || "Organization",
    name: post.author || siteData.siteName,
    url: siteData.siteUrl,
  },
  publisher: {
    "@type": "Organization",
    "@id": `${siteData.siteUrl}/#organization`,
    name: siteData.siteName,
    url: siteData.siteUrl,
    logo: {
      "@type": "ImageObject",
      url: absoluteUrl(siteData.defaultShareImage),
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": absoluteUrl(normalizePath(path)),
  },
  keywords: post.tags ? post.tags.join(", ") : undefined,
  // GEO: inLanguage for AI language understanding
  inLanguage: "en",
  // GEO: wordCount for content depth signals
  ...(post.wordCount ? { wordCount: post.wordCount } : {}),
  // GEO: articleSection for topic categorization
  ...(post.category ? { articleSection: post.category } : {}),
  // GEO: speakable for voice / AI answer optimization
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: [".blog-post-excerpt", ".blog-post-content h2", ".blog-post-content p:first-of-type"],
  },
});

// GEO: SoftwareApplication schema for products (Lumina, CareIPD)
const createSoftwareApplicationSchema = ({
  name,
  description,
  path,
  imageUrl,
  applicationCategory = "BusinessApplication",
  operatingSystem = "Web, Windows, macOS, iOS, Android",
  offers,
}) => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name,
  description,
  url: absoluteUrl(normalizePath(path)),
  applicationCategory,
  operatingSystem,
  ...(imageUrl ? { image: absoluteUrl(imageUrl) } : {}),
  author: {
    "@type": "Organization",
    "@id": `${siteData.siteUrl}/#organization`,
    name: siteData.siteName,
  },
  ...(offers ? { offers } : {}),
});

// GEO: ItemList schema for AI service discovery
const createItemListSchema = ({ name, description, items = [] }) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name,
  description,
  numberOfItems: items.length,
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    url: absoluteUrl(normalizePath(item.path)),
    ...(item.description ? { description: item.description } : {}),
  })),
});

// GEO: AggregateRating schema for trust signals
const createAggregateRatingSchema = ({
  ratingValue = "4.9",
  reviewCount = "50",
  bestRating = "5",
  worstRating = "1",
} = {}) => ({
  "@type": "AggregateRating",
  ratingValue,
  reviewCount,
  bestRating,
  worstRating,
});

module.exports = {
  absoluteUrl,
  createAggregateRatingSchema,
  createBreadcrumbList,
  createBlogPostingSchema,
  createContactPageSchema,
  createFaqPageSchema,
  createHowToSchema,
  createItemListSchema,
  createLocalBusinessSchema,
  createOrganizationSchema,
  createProfessionalServiceSchema,
  createServiceSchema,
  createSoftwareApplicationSchema,
  createWebPageSchema,
  createWebSiteSchema,
  normalizePath,
  stripWhitespace,
  truncate,
};

module.exports.default = module.exports;